const environment = import.meta.env.VITE_ENVIRONMENT;
import environments from '../../environments/environments';
import CryptoJS from 'crypto-js';

const envConfig = environments.env;
const envSiteConfig = environments.envSite;

export function validateID(id: string): boolean {
  if (!id) return false;
  if (id.length !== 10) {
    return false;
  }

  const regionDigit = Number(id.substring(0, 2));
  if (regionDigit < 0 || regionDigit > 24) {
    return false;
  }

  const evens = Number(id[1]) + Number(id[3]) + Number(id[5]) + Number(id[7]);
  let odds = 0;
  for (let i = 0; i < 9; i += 2) {
    let num = Number(id[i]) * 2;
    if (num > 9) num -= 9;
    odds += num;
  }

  const totalSum = evens + odds;

  const nextTen = Math.ceil(totalSum / 10) * 10;

  let checkDigit = nextTen - totalSum;
  if (checkDigit === 10) checkDigit = 0;

  const lastDigit = Number(id[9]);
  return checkDigit === lastDigit;
}

export function validateRUC(
  ruc: string,
  translations: { naturalPerson: string; publicCompany: string; privateCompany: string },
): { isValid: boolean; type: string | null } {
  if (ruc.length !== 13) {
    return { isValid: false, type: null };
  }

  const idPart = ruc.slice(0, 10);
  const provinceCode = parseInt(ruc.slice(0, 2), 10);
  const thirdDigit = parseInt(ruc[2], 10);
  const establishmentCode = parseInt(ruc.slice(10, 13), 10);

  if (provinceCode < 1 || provinceCode > 24) {
    return { isValid: false, type: null };
  }

  if (establishmentCode < 1) {
    return { isValid: false, type: null };
  }

  let type: string | null = null;

  if (thirdDigit >= 0 && thirdDigit <= 5) {
    const isValidID = validateID(idPart);
    if (isValidID) {
      type = translations.naturalPerson;
    } else {
      return { isValid: false, type: null };
    }
  } else if (thirdDigit === 6) {
    type = translations.publicCompany;
  } else if (thirdDigit === 9) {
    type = translations.privateCompany;
  } else {
    return { isValid: false, type: null };
  }

  return { isValid: true, type };
}

export function validatePassport(passport: string): boolean {
  if (!passport || passport?.trim() === '') {
    return false;
  }
  const passportRegex = /^[A-Z0-9]{6,15}$/;
  return passportRegex.test(passport.toUpperCase());
}

export function updateInput($event: any) {
  const target = $event.originalEvent.target as HTMLElement;
  target.blur();
  target.focus();
}

export function convertTextUppercaseLowercase(data, exclude: string[] = [], uppercase: boolean = true) {
  for (const key in data) {
    if (typeof data[key] === 'string' && !exclude.includes(key)) {
      data[key] = uppercase ? data[key].toUpperCase() : data[key].toLowerCase();
    }
  }
  return data;
}

export function getEnvironment() {
  const config =
    environment == 'DEV'
      ? envConfig.urlDev
      : environment == 'PROD'
        ? envConfig.urlProd
        : environment == 'CERT'
          ? envConfig.urlCert
          : envConfig.url;
  return config;
}

export function getSiteEnvironment() {
  const config =
    environment == 'DEV'
      ? envSiteConfig.urlDev
      : environment == 'PROD'
        ? envSiteConfig.urlProd
        : environment == 'CERT'
          ? envSiteConfig.urlCert
          : envSiteConfig.url;
  return config;
}

export function encrypteData(data) {
  if (data) {
    try {
      const key = CryptoJS.PBKDF2(environments.keyValueCrypto, 'salt', { keySize: 256 / 32, iterations: 100 });
      const iv = CryptoJS.enc.Utf8.parse(environments.ivKeyCrypto); // Convert string to WordArray
      const encrypted = CryptoJS.AES.encrypt(data, key, { iv: iv, mode: CryptoJS.mode.CBC });
      return encrypted.ciphertext.toString(CryptoJS.enc.Hex);
    } catch (error) {
      return null;
    }
  }
}

export function decrypteData(data) {
  if (data) {
    try {
      const key = CryptoJS.PBKDF2(environments.keyValueCrypto, 'salt', { keySize: 256 / 32, iterations: 100 });
      const iv = CryptoJS.enc.Utf8.parse(environments.ivKeyCrypto);
      const decrypted = CryptoJS.AES.decrypt({ ciphertext: CryptoJS.enc.Hex.parse(data) }, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
      });
      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      return null;
    }
  }
}

/**
 * Elimina propiedades con valores vacíos o nulos de un objeto
 * @param {Object} obj - El objeto a limpiar
 * @param {Array<string>} excludeKeys - Lista de claves que no deben ser eliminadas aunque estén vacías
 * @param {boolean} deep - Si es true, limpia también objetos anidados
 * @returns {Object} - Un nuevo objeto sin las propiedades vacías o nulas
 */
export function cleanEmptyValues(obj: any, excludeKeys: string[] = [], deep: boolean = true) {
  // Si no es un objeto o es null, retornar el valor original
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  // Crear un nuevo objeto para no modificar el original
  const result = {};

  // Recorrer todas las propiedades del objeto
  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    // Si la clave está en la lista de exclusiones, mantenerla sin importar su valor
    if (excludeKeys.includes(key)) {
      result[key] = value;
      return;
    }

    // Verificar si el valor es vacío o nulo
    const isEmpty = value === null || value === undefined || value === '';

    if (!isEmpty) {
      // Si es un objeto anidado y deep es true, limpiarlo recursivamente
      if (deep && typeof value === 'object' && !Array.isArray(value)) {
        const cleanedValue = cleanEmptyValues(value, excludeKeys, deep);

        // Solo agregar el objeto anidado si tiene propiedades después de limpiarlo
        if (Object.keys(cleanedValue).length > 0) {
          result[key] = cleanedValue;
        }
      } else {
        // Para valores que no son objetos o si deep es false
        result[key] = value;
      }
    }
  });

  return result;
}
